import { useFormik } from "formik"

import {
  useFetchProducts,
  useFetchShippingDestinations,
} from "src/data/ecommerce/ecommerceQueries"
import { usePostParadiseQuote } from "src/data/paradise/paradiseQuotes/paradiseQuoteQueries"
import { getSearchParam } from "src/router/searchParamsUtils"

type TParadiseCreateQuoteForm = {
  customerName: string
  shippingCountry: string
  expiresAt: Date | null
  customerNote: string
  userId: string
  cart: {
    planId: string
    prepaidHomeQuantity: number
    planAssociatedShippableProducts: {
      productId: string
      quantity: number
    }[]
    additionalShippableProducts: {
      productId: string
      quantity: number
    }[]
    discountCode: string
  }
}

const initialCustomerNote =
  "Here is your quote. Let me know if you have any questions.\n\nBest regards,  \nMinut"

export const initialParadiseCreateQuoteValues: TParadiseCreateQuoteForm = {
  customerName: "",
  shippingCountry: "",
  expiresAt: null,
  customerNote: initialCustomerNote,
  userId: "",
  cart: {
    planId: "",
    prepaidHomeQuantity: 0,
    planAssociatedShippableProducts: [],
    additionalShippableProducts: [],
    discountCode: "",
  },
}

function getInitialValues(): TParadiseCreateQuoteForm {
  const prefilledUserId = getSearchParam({
    key: "create_quote_user_id",
    type: "string",
  })

  return {
    ...initialParadiseCreateQuoteValues,
    userId: prefilledUserId || initialParadiseCreateQuoteValues.userId,
  }
}

export function useParadiseCreateQuote() {
  const form = useFormik<TParadiseCreateQuoteForm>({
    initialValues: getInitialValues(),
    onSubmit: createQuote,
  })

  const postParadiseQuote = usePostParadiseQuote()

  const fetchShippingDestinations = useFetchShippingDestinations()

  const selectedDestination = fetchShippingDestinations.data?.find(
    (destination) => destination.country_code === form.values.shippingCountry
  )
  const fetchProducts = useFetchProducts({
    filter: {
      currency_code: selectedDestination?.currency_code,
    },
    options: {
      enabled: !!selectedDestination,
    },
  })

  const selectedProduct = fetchProducts.data?.products.find(
    (product) => product.plan_id === form.values.cart.planId
  )
  const selectedPlanAssociatedShippableProducts =
    selectedProduct?.shippable_products.filter(
      (product) => product.type === "plan_associated"
    )
  const selectedAdditionalShippableProducts =
    selectedProduct?.shippable_products.filter(
      (product) => product.type === "additional"
    )

  function setValues(values: Partial<TParadiseCreateQuoteForm>) {
    form.setValues((prev) => ({
      ...prev,
      ...values,
    }))
  }

  function createQuote(values: TParadiseCreateQuoteForm) {
    postParadiseQuote.mutate({
      customer_name: values.customerName,
      expires_at: values.expiresAt?.toISOString() || "",
      customer_note: values.customerNote,
      cart: {
        plan_id: values.cart.planId,
        prepaid_home_quantity: values.cart.prepaidHomeQuantity,
        plan_associated_shippable_products:
          values.cart.planAssociatedShippableProducts.map((product) => ({
            product_id: product.productId,
            quantity: product.quantity,
          })),
        additional_shippable_products:
          values.cart.additionalShippableProducts.map((product) => ({
            product_id: product.productId,
            quantity: product.quantity,
          })),
        discount_code: values.cart.discountCode || undefined,
      },
    })
  }

  return {
    form: {
      ...form,
      setValues,
    },
    shippingDestinations: fetchShippingDestinations.data,
    isLoadingShippingDestinations: fetchShippingDestinations.isLoading,
    products: fetchProducts.data?.products,
    isInitialLoadingProducts: fetchProducts.isInitialLoading,
    selectedProduct,
    selectedPlanAssociatedShippableProducts,
    selectedAdditionalShippableProducts,
    postParadiseQuoteIsLoading: postParadiseQuote.isLoading,
    postParadiseQuoteData: postParadiseQuote.data,
    postParadiseQuoteError: postParadiseQuote.error,
    resetPostParadiseQuote: postParadiseQuote.reset,
  }
}
